import { useCallback, useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pagination, usePagination } from "@/components/Pagination";
import { Download, FileText, Inbox } from "lucide-react";
import { Layout } from "@/components/Layout";

const fmtMoney = (v) =>
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" }).format(Number(v || 0));

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function Payslips() {
  const [linked, setLinked] = useState(true);
  const [rows, setRows] = useState([]);
  const [ytd, setYtd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/payslips/me");
      setLinked(data.linked);
      setRows(data.payslips || []);
      setYtd(data.ytd || null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pg = usePagination(rows, 10);

  const downloadPdf = async (id) => {
    setDownloadingId(id);
    try {
      const resp = await api.get(`/payslips/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const period = rows.find((r) => r.id === id)?.period_end || id;
      a.download = `payslip-${period}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // Surface the friendly backend error if present.
      const detail = e?.response?.data?.detail || "Failed to download PDF";
      alert(detail);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="payslips-page">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#1A2744]">Payslips</h1>
            <p className="text-sm text-gray-500 mt-1">
              Imported daily from Xero · view and download branded PDFs.
            </p>
          </div>
          <FileText className="h-8 w-8 text-[#C9A84C]" />
        </header>

        {linked && ytd && ytd.payslip_count > 0 && (
          <Card className="border-[#1A2744]/10 bg-gradient-to-br from-[#1A2744] to-[#2a3a5e] text-white" data-testid="ytd-card">
            <CardContent className="p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#C9A84C] font-semibold">
                    Year-to-date · NZ tax year {ytd.label}
                  </div>
                  <div className="text-xs text-white/60 mt-1">
                    {ytd.payslip_count} payslip{ytd.payslip_count === 1 ? "" : "s"} · {fmtDate(ytd.tax_year_start)} → {fmtDate(ytd.tax_year_end)}
                  </div>
                </div>
                <span className="text-[10px] text-white/50">Useful for IRD reconciliation, rental applications, etc.</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div data-testid="ytd-gross">
                  <div className="text-[10px] uppercase tracking-widest text-white/60 font-semibold">Gross earned</div>
                  <div className="font-heading text-2xl font-bold mt-1">{fmtMoney(ytd.gross)}</div>
                </div>
                <div data-testid="ytd-tax">
                  <div className="text-[10px] uppercase tracking-widest text-white/60 font-semibold">Tax paid</div>
                  <div className="font-heading text-2xl font-bold mt-1">−{fmtMoney(ytd.tax)}</div>
                </div>
                <div data-testid="ytd-kiwisaver">
                  <div className="text-[10px] uppercase tracking-widest text-white/60 font-semibold">KiwiSaver</div>
                  <div className="font-heading text-2xl font-bold mt-1">−{fmtMoney(ytd.kiwisaver)}</div>
                </div>
                <div data-testid="ytd-net">
                  <div className="text-[10px] uppercase tracking-widest text-[#C9A84C] font-semibold">Net take-home</div>
                  <div className="font-heading text-2xl font-bold mt-1 text-[#C9A84C]">{fmtMoney(ytd.net)}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!linked && !loading && (
          <Card className="border-amber-200 bg-amber-50" data-testid="payslips-not-linked">
            <CardContent className="p-5 flex items-start gap-3">
              <Inbox className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
              <div className="text-sm text-amber-900">
                <div className="font-semibold">Your account isn't linked to Xero yet.</div>
                <p className="text-amber-800/80 mt-1">
                  Ask your café admin to link your record to Xero Payroll so your payslips show up here. Until then this page will stay empty.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {linked && rows.length === 0 && !loading && (
          <Card className="border-gray-200" data-testid="payslips-empty">
            <CardContent className="p-10 text-center">
              <Inbox className="h-10 w-10 text-gray-300 mx-auto" />
              <div className="font-heading text-lg font-bold text-[#1A2744] mt-3">No payslips yet</div>
              <p className="text-sm text-gray-500 mt-1">
                Once your first pay run is posted in Xero, your payslip will appear here within 24 hours.
              </p>
            </CardContent>
          </Card>
        )}

        {rows.length > 0 && (
          <Card className="border-gray-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pay period</TableHead>
                    <TableHead>Payment date</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Tax</TableHead>
                    <TableHead className="text-right">Deductions</TableHead>
                    <TableHead className="text-right">Net pay</TableHead>
                    <TableHead className="text-right">PDF</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pg.pageItems.map((r) => (
                    <TableRow key={r.id} data-testid={`payslip-row-${r.id}`}>
                      <TableCell className="font-medium text-[#1A2744]">
                        {fmtDate(r.period_start)} → {fmtDate(r.period_end)}
                      </TableCell>
                      <TableCell>{fmtDate(r.payment_date)}</TableCell>
                      <TableCell className="text-right font-medium">{fmtMoney(r.gross_earnings)}</TableCell>
                      <TableCell className="text-right text-gray-600">−{fmtMoney(r.total_taxes)}</TableCell>
                      <TableCell className="text-right text-gray-600">
                        −{fmtMoney(Number(r.total_deductions || 0) + Number(r.total_super || 0))}
                      </TableCell>
                      <TableCell className="text-right font-bold text-emerald-700">{fmtMoney(r.total_pay)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadPdf(r.id)}
                          disabled={downloadingId === r.id}
                          data-testid={`payslip-download-${r.id}`}
                        >
                          <Download className={`h-3.5 w-3.5 mr-1 ${downloadingId === r.id ? "animate-pulse" : ""}`} />
                          {downloadingId === r.id ? "…" : "PDF"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="p-4 border-t border-gray-100">
                <Pagination
                  page={pg.page}
                  pageCount={pg.pageCount}
                  total={pg.total}
                  pageSize={pg.pageSize}
                  onPageChange={pg.setPage}
                  idPrefix="payslips"
                />
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
